import React, { Fragment, useEffect, useState, } from 'react';
import { Button, Modal, ModalBody, ModalHeader, ModalFooter, Form, } from 'reactstrap';
import { uniqBy, pick, get, isEmpty, mapValues, isFunction, } from 'lodash';
import { useToggle, useAsync, } from 'react-use';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router';
import dedent from 'dedent';

import firebase, { functions } from '../../firebase';
import { title, smallImageUrl, asin, condition, } from '../../shared/models/amazonProduct';
import { amazonProductFields } from '../../shared/models/product';
import { idWithTenant } from '../../shared/util';
import { createProductsWithSkuCheck } from '../../util';
import useFormState from '../hooks/useFormState';
import usePrevious from '../hooks/usePrevious';
import Field from '../Field';
import OverlayLoading from '../OverlayLoading';

const db = firebase.firestore();
const searchAmazonProducts = functions.httpsCallable('searchAmazonProducts');
const { entries, } = Object;
const postAmazonProducts = functions.httpsCallable('postAmazonProducts');
const fetchAmazonProducts = functions.httpsCallable('fetchAmazonProducts');

export default function AmazonProductAddModal(props) {
  const { extraSellerId, newRef, tenant, isOpen, values, otherProducts, onClickClose, renderFooterContent = _ => _, } = props;
  const history = useHistory();
  const [selectedAmazonProduct, selectAmazonProduct] = useState();
  const statedFields = useFormState(values, amazonProductFields({ otherProducts }), isOpen);
  const isUnsubmittable = Object.values(statedFields).some(_ => !_.isValid);
  const [isSubmitting, toggleSubmitting] = useToggle(false);
  const { value: amazonProducts = [], loading } = useAsync(async () => {
    if(isEmpty(statedFields.query.value)) return [];
    const { data: amazonProducts } = await searchAmazonProducts({ query: statedFields.query.value });
    const { data: amazonProductsByAsin } = await fetchAmazonProducts({ idType: 'ASIN', ids: [statedFields.query.value], });
    return uniqBy([...amazonProducts, ...(amazonProductsByAsin || [])].filter(_ => _?.AttributeSets), asin);
  }, [statedFields.query.value]);
  const onSubmit = async (event) => {
    event.preventDefault();
    if(isUnsubmittable) return;

    toggleSubmitting(true);
    const { data: [amazonProductBySku] } = await fetchAmazonProducts({ tenantId: tenant.id, idType: 'SKU', ids: [statedFields.sku.value], });
    if(amazonProductBySku != null) {
      toast.error(dedent`すでにこのSKUはAmazonへ登録されています。SKUを変更して登録ください。
        または該当SKUのAmazon情報を取り込んで登録し、入出荷依頼してください。`);
      return toggleSubmitting(false);
    }

    const values = mapValues(statedFields, 'value');
    const itemName = values.itemName || title(selectedAmazonProduct).slice(0, 10);
    try {
      await postAmazonProducts({
        extraSellerId,
        tenantId: tenant.id,
        products: [{
          ...values,
          asin: asin(selectedAmazonProduct),
          amazonProduct: selectedAmazonProduct,
        }],
      });
      await createProductsWithSkuCheck(db, newRef.parent, [{
        productId: newRef.id,
        sku: values.sku,
        data: {
          asin: asin(selectedAmazonProduct),
          isAmazonProduct: true,
          fnsku: null,
          postedInventoryAvailabilityCount: 0,
          amazonProduct: selectedAmazonProduct,
          ...values,
          name: title(selectedAmazonProduct),
          itemName,
          requestsNeko: false,
          goodInventory: 0,
          ngInventory: 0,
          shippingSize: null,
          extraSellerId,
          createdAt: new Date(),
        },
      }]);
      onClickClose();
      toast.success('登録しました');
    } catch(e) {
      console.error(e);
      toast.error(e.message || '失敗しました');
    }
    toggleSubmitting(false);
  };
  useEffect(() => {
    selectAmazonProduct(null);
  }, [statedFields.query.value]);

  return (
    <Modal isOpen={isOpen} className="position-relative" style={{ minWidth: 700 }}>
      <ModalHeader>
        Amazonから商品を検索して登録
      </ModalHeader>
      <Form onSubmit={onSubmit}>
        <ModalBody className="overflow-auto" style={{ maxHeight: '70vh', }}>
          <Field name="query" {...statedFields.query} />
          {
            loading && (
              <div className="d-flex justify-content-center">
                <span className="fas fa-spin fa-spinner" />
              </div>
            )
          }
          {
            amazonProducts.length > 0 && selectedAmazonProduct == null && (
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ minWidth: 200 }}>商品名</th>
                    <th>商品画像</th>
                    <th>ASIN</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {
                    amazonProducts.map((amazonProduct, i) => {
                      const onClickSelect = () => {
                        selectAmazonProduct(amazonProduct);
                      };

                      return (
                        <tr key={i}>
                          <td className="text-break">
                            {title(amazonProduct)}
                          </td>
                          <td>
                            <img src={smallImageUrl(amazonProduct)} style={{ maxWidth: 100, maxHeight: 100, }} />
                          </td>
                          <td>{asin(amazonProduct)}</td>
                          <td className="text-nowrap">
                            <Button color="primary" onClick={onClickSelect}>
                              選択
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  }
                </tbody>
              </table>
            )
          }
          {
            selectedAmazonProduct != null && (
              <div>
                <div className="card">
                  <div className="card-header">
                    選択した商品
                  </div>
                  <div className="card-body">
                    <table className="table table-bordered">
                      <tbody className="thead-light">
                        <tr>
                          <th style={{ minWidth: 200 }}>商品名</th>
                          <td>{title(selectedAmazonProduct)}</td>
                        </tr>
                        <tr style={{ minWidth: 200 }}>
                          <th>商品画像</th>
                          <td>
                            <img src={smallImageUrl(selectedAmazonProduct)} style={{ maxWidth: 100, maxHeight: 100 }} />
                          </td>
                        </tr>
                        <tr style={{ minWidth: 200 }}>
                          <th>ASIN</th>
                          <td>{asin(selectedAmazonProduct)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="mt-2">
                  {
                    entries(pick(statedFields, ['sku', 'itemName', 'price', 'deliveryRoute', 'condition', 'conditionNote'])).map(([fieldName, fieldSetting]) => (
                      <Field
                        key={fieldName}
                        name={fieldName}
                        {...fieldSetting}
                      />
                    ))
                  }
                </div>
              </div>
            )
          }
        </ModalBody>
        <ModalFooter>
          {
            renderFooterContent((
              <Fragment>
                {onClickClose && <Button className="cancel" color="secondary" onClick={onClickClose}>閉じる</Button>}
                <Button className="save" type="submit" color="primary" onClick={onSubmit} disabled={isUnsubmittable || isSubmitting}>保存</Button>
              </Fragment>
            ), { onSubmit, onClickClose, isUnsubmittable, isSubmitting, })
          }
        </ModalFooter>
      </Form>
      <OverlayLoading isOpen={isSubmitting} label="処理中..." />
    </Modal>
  );
};

